import React from 'react';
import { User as UserIcon, MapPin, Briefcase, Building, FileText, TrendingUp, Users } from 'lucide-react';

const GlobalSearchResults = ({ results, onViewProfile, onSelectJob, onSelectPost, onViewCompany, onClose }) => {
  const { people = [], jobs = [], posts = [], companies = [] } = results || {};

  const totalResults = people.length + jobs.length + posts.length + companies.length;

  if (totalResults === 0) {
    return null;
  }

  return (
    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 max-h-[70vh] overflow-y-auto z-50">
      {/* People Section */}
      {people.length > 0 && (
        <div className="border-b border-gray-200">
          <div className="px-4 py-3 bg-gray-50">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <UserIcon size={16} className="text-gray-600" />
              People
            </h4>
          </div>
          <div>
            {people.map((person) => (
              <div
                key={person.id}
                className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 transition cursor-pointer border-b border-gray-100 last:border-b-0"
                onClick={() => {
                  onViewProfile && onViewProfile(person.id);
                  onClose();
                }}
              >
                <div className="w-14 h-14 bg-[#DBEAFE] rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {person.profile_picture ? (
                    <img src={person.profile_picture} alt={person.name} className="w-full h-full object-cover" />
                  ) : (
                    <UserIcon size={24} className="text-[#4169E1]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h5 className="font-semibold text-gray-900 text-base truncate">{person.name}</h5>
                  {person.headline && <p className="text-sm text-gray-600 truncate mt-0.5">{person.headline}</p>}
                  <div className="flex items-center gap-3 mt-1">
                    {person.location && (
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <MapPin size={12} />
                        {person.location}
                      </p>
                    )}
                    {person.connections_count > 0 && (
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Users size={12} />
                        {person.connections_count} connections
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Jobs Section */}
      {jobs.length > 0 && (
        <div className="border-b border-gray-200">
          <div className="px-4 py-3 bg-gray-50">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Briefcase size={16} className="text-gray-600" />
              Jobs
            </h4>
          </div>
          <div>
            {jobs.map((job) => (
              <div
                key={job.id}
                className="flex items-start gap-4 px-4 py-3 hover:bg-gray-50 transition cursor-pointer border-b border-gray-100 last:border-b-0"
                onClick={() => {
                  onSelectJob && onSelectJob(job);
                  onClose();
                }}
              >
                <div className="w-14 h-14 bg-white border border-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {job.company_logo ? (
                    <img src={job.company_logo} alt={job.company_name} className="w-full h-full object-contain p-1" />
                  ) : (
                    <Building size={24} className="text-gray-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h5 className="font-semibold text-gray-900 text-base truncate">{job.title}</h5>
                  <p className="text-sm text-gray-700 mt-0.5">{job.company_name}</p>
                  <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-600 flex-wrap">
                    {job.location_city && (
                      <span className="flex items-center gap-1">
                        <MapPin size={12} />
                        {job.location_city}, {job.location_state}
                      </span>
                    )}
                    {job.is_remote && (
                      <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                        Remote
                      </span>
                    )}
                    {job.employment_type && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs">
                        {job.employment_type}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Companies Section */}
      {companies.length > 0 && (
        <div className="border-b border-gray-200">
          <div className="px-4 py-3 bg-gray-50">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Building size={16} className="text-gray-600" />
              Companies
            </h4>
          </div>
          <div>
            {companies.map((company) => (
              <div
                key={company.id}
                className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 transition cursor-pointer border-b border-gray-100 last:border-b-0"
                onClick={() => {
                  onViewCompany && onViewCompany(company.id);
                  onClose();
                }}
              >
                <div className="w-14 h-14 bg-white border border-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {company.logo_url ? (
                    <img src={company.logo_url} alt={company.name} className="w-full h-full object-contain p-1" />
                  ) : (
                    <Building size={24} className="text-gray-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h5 className="font-semibold text-gray-900 text-base truncate">{company.name}</h5>
                  {company.industry && <p className="text-sm text-gray-600 truncate mt-0.5">{company.industry}</p>}
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    {company.location && (
                      <span className="flex items-center gap-1">
                        <MapPin size={12} />
                        {company.location}
                      </span>
                    )}
                    {company.jobs_count > 0 && (
                      <span className="flex items-center gap-1">
                        <Briefcase size={12} />
                        {company.jobs_count} {company.jobs_count === 1 ? 'job' : 'jobs'}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Follow company action - you can implement this later
                  }}
                  className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 hover:border-gray-400 transition text-sm font-semibold flex-shrink-0"
                >
                  Follow
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Posts Section */}
      {posts.length > 0 && (
        <div>
          <div className="px-4 py-3 bg-gray-50">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <FileText size={16} className="text-gray-600" />
              Posts
            </h4>
          </div>
          <div>
            {posts.map((post) => (
              <div
                key={post.id}
                className="flex items-start gap-4 px-4 py-3 hover:bg-gray-50 transition cursor-pointer border-b border-gray-100 last:border-b-0"
                onClick={() => {
                  onSelectPost && onSelectPost(post);
                  onClose();
                }}
              >
                <div className="w-14 h-14 bg-[#DBEAFE] rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {post.user_picture ? (
                    <img src={post.user_picture} alt={post.user_name} className="w-full h-full object-cover" />
                  ) : (
                    <UserIcon size={24} className="text-[#4169E1]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h5 className="font-semibold text-gray-900 text-base">{post.user_name}</h5>
                  <p className="text-sm text-gray-600 line-clamp-2 mt-1 leading-relaxed">
                    {post.content?.replace(/<[^>]*>/g, '').substring(0, 150)}
                    {post.content?.length > 150 && '...'}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <TrendingUp size={12} />
                      {post.likes_count || 0} {(post.likes_count || 0) === 1 ? 'like' : 'likes'}
                    </span>
                    <span>
                      {post.comments_count || 0} {(post.comments_count || 0) === 1 ? 'comment' : 'comments'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearchResults;
